import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FacebookPageAnalyticsPostsByDate.graphql';
import { Query, QueryfacebookPageAnalyticsPostsByDateArgs } from '../../__generated__/globalTypes';

export const useFacebookPageAnalyticsPostsByDateQuery = (
  options?: QueryHookOptions<Query, QueryfacebookPageAnalyticsPostsByDateArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfacebookPageAnalyticsPostsByDateArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFacebookPageAnalyticsPostsByDateLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsByDateArgs>
): QueryResult<Query, QueryfacebookPageAnalyticsPostsByDateArgs> & {
  getFacebookPageAnalyticsPostsByDate: LazyQueryExecFunction<Query, QueryfacebookPageAnalyticsPostsByDateArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFacebookPageAnalyticsPostsByDate, result] = useLazyQuery<Query, QueryfacebookPageAnalyticsPostsByDateArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getFacebookPageAnalyticsPostsByDate, ...result };
};

export const useFacebookPageAnalyticsPostsByDatePromiseQuery = () => {
  const client = useApolloClient();

  const getFacebookPageAnalyticsPostsByDate = useCallback(
    (options?: Omit<QueryOptions<QueryfacebookPageAnalyticsPostsByDateArgs, Query>, 'query'>) =>
      client.query<Query, QueryfacebookPageAnalyticsPostsByDateArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFacebookPageAnalyticsPostsByDate };
};
// Suspense Query Hooks
export const useFacebookPageAnalyticsPostsByDateSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsByDateArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfacebookPageAnalyticsPostsByDateArgs>(QUERY, {
    ...options
  });

  return result;
};
